import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, BuildingLibraryIcon, MapPinIcon, AcademicCapIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { fetchUniversities, fetchUniversityById, createUniversity, updateUniversity, deleteUniversity, clearSelectedUniversity } from '../../store/slices/universitySlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, Badge, SearchInput, Pagination, ConfirmDialog, Card, Modal, Input, Select } from '../../components/Common';
import { systemAPI, categoryAPI } from '../../services/api';

const UniversityList = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { universities, selectedUniversity, loading, pagination } = useSelector((state) => state.university);
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({
    name: '', description: '', established_on: '', students_capacity: '',
    numbers_of_faculty: '', country: '', state: '', city: '', zip_code: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Dynamic filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    country: '',
    withCourses: false,
    category: ''
  });
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);

  // Load countries and categories for filters
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [countriesRes, categoriesRes] = await Promise.all([
          systemAPI.getCountries(),
          categoryAPI.getDropdown()
        ]);
        setCountries(countriesRes.data.data || []);
        setCategories(categoriesRes.data.data || []);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  useEffect(() => {
    const params = { 
      page, 
      size: pageSize, 
      search,
      withCourses: filters.withCourses ? 'true' : 'false',
      country: filters.country,
      category: filters.category
    };
    dispatch(fetchUniversities(params));
  }, [dispatch, page, pageSize, search, filters]);

  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      openModal();
    }
  }, [searchParams]);

  const openModal = (university = null) => {
    if (university) {
      setEditingUniversity(university);
      setFormData({
        name: university.name || '', description: university.description || '',
        established_on: university.established_on || '', students_capacity: university.students_capacity || '',
        numbers_of_faculty: university.numbers_of_faculty || '', country: university.country || '',
        state: university.state || '', city: university.city || '', zip_code: university.zip_code || ''
      });
    } else {
      setEditingUniversity(null);
      setFormData({
        name: '', description: '', established_on: '', students_capacity: '',
        numbers_of_faculty: '', country: '', state: '', city: '', zip_code: ''
      });
    }
    setModalOpen(true);
  };

  const openDetailsModal = async (university) => {
    try {
      await dispatch(fetchUniversityById({ id: university.id, includeCourses: true })).unwrap();
      setDetailsModalOpen(true);
    } catch (error) {
      dispatch(showError('Failed to load university details'));
    }
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    dispatch(clearSelectedUniversity());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.description.trim()) return;

    setSubmitting(true);
    try {
      const submitData = { ...formData };
      if (submitData.students_capacity) submitData.students_capacity = parseInt(submitData.students_capacity);
      if (submitData.numbers_of_faculty) submitData.numbers_of_faculty = parseInt(submitData.numbers_of_faculty);

      if (editingUniversity) {
        await dispatch(updateUniversity({ id: editingUniversity.id, data: submitData })).unwrap();
        dispatch(showSuccess('University updated successfully'));
      } else {
        await dispatch(createUniversity(submitData)).unwrap();
        dispatch(showSuccess('University created successfully'));
      }
      setModalOpen(false);
      dispatch(fetchUniversities({ page, size: pageSize, search, withCourses: filters.withCourses ? 'true' : 'false' }));
    } catch (error) {
      console.error('Submit error:', error);
      dispatch(showError(error || 'Failed to save university'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteUniversity(deleteModal.id)).unwrap();
        dispatch(showSuccess('University deleted successfully'));
        dispatch(fetchUniversities({ page, size: pageSize, search, withCourses: filters.withCourses ? 'true' : 'false' }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete university'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({ country: '', withCourses: false, category: '' });
    setPage(1);
  };

  const columns = [
    {
      key: 'name',
      title: 'University',
      render: (value, row) => (
        <div>
          <p className="font-medium text-slate-800 line-clamp-1">{value || 'Unnamed'}</p>
          <p className="text-sm text-slate-500 line-clamp-1">{row.description || 'No description'}</p>
        </div>
      ),
    },
    {
      key: 'location',
      title: 'Location',
      render: (_, row) => (
        <div className="flex items-center gap-1 text-slate-600">
          <MapPinIcon className="w-4 h-4" />
          <span>{[row.city, row.state, row.country].filter(Boolean).join(', ') || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'courses',
      title: 'Courses',
      render: (value, row) => {
        const courseCount = row.courses?.length || 0;
        return courseCount > 0 ? (
          <button 
            onClick={() => openDetailsModal(row)}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
          >
            <AcademicCapIcon className="w-4 h-4" />
            <span>{courseCount} {courseCount === 1 ? 'course' : 'courses'}</span>
          </button>
        ) : (
          <span className="text-slate-400">No courses</span>
        );
      },
    },
    {
      key: 'students_capacity',
      title: 'Students',
      render: (value) => value ? <Badge variant="info">{value.toLocaleString()}</Badge> : '-',
    },
    {
      key: 'numbers_of_faculty',
      title: 'Faculty',
      render: (value) => value ? <Badge variant="success">{value.toLocaleString()}</Badge> : '-',
    },
    {
      key: 'established_on',
      title: 'Established',
      render: (value) => value ? new Date(value).getFullYear() : '-',
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openDetailsModal(row)} className="p-2 hover:bg-blue-50 rounded-lg" title="View Details">
            <BuildingLibraryIcon className="w-4 h-4 text-blue-500" />
          </button>
          <Link to={`/universities/edit/${row.id}`} className="p-2 hover:bg-slate-100 rounded-lg inline-block" title="Edit">
            <PencilIcon className="w-4 h-4 text-slate-500" />
          </Link>
          <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Universities</h1>
          <p className="text-slate-500">Manage university listings with dynamic relationships</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            icon={FunnelIcon} 
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters {(filters.country || filters.category || filters.withCourses) && '(Active)'}
          </Button>
          <Link to="/universities/new"><Button icon={PlusIcon}>New University</Button></Link>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Country"
              value={filters.country}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              options={[
                { value: '', label: 'All Countries' },
                ...countries.map(c => ({ value: c.name, label: c.name }))
              ]}
            />
            <Select
              label="Category (by courses)"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              options={[
                { value: '', label: 'All Categories' },
                ...categories.map(c => ({ value: c.id, label: c.name }))
              ]}
            />
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.withCourses}
                  onChange={(e) => handleFilterChange('withCourses', e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">Show courses</span>
              </label>
              <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
            </div>
          </div>
        </Card>
      )}

      <Card noPadding>
        <div className="p-4 border-b border-slate-100">
          <SearchInput value={search} onChange={setSearch} placeholder="Search universities..." className="max-w-md" />
        </div>

        <Table columns={columns} data={universities} loading={loading} emptyMessage="No universities found" emptyIcon={BuildingLibraryIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* University Form Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingUniversity ? 'Edit University' : 'New University'} size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={submitting}>{editingUniversity ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="University name" required />
          <Input label="Description" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Description" required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Established On" type="date" value={formData.established_on} onChange={(e) => setFormData(prev => ({ ...prev, established_on: e.target.value }))} />
            <Input label="Student Capacity" type="number" value={formData.students_capacity} onChange={(e) => setFormData(prev => ({ ...prev, students_capacity: e.target.value }))} placeholder="e.g., 10000" />
          </div>
          <Input label="Number of Faculty" type="number" value={formData.numbers_of_faculty} onChange={(e) => setFormData(prev => ({ ...prev, numbers_of_faculty: e.target.value }))} placeholder="e.g., 500" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Country" value={formData.country} onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))} placeholder="Country" />
            <Input label="State" value={formData.state} onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))} placeholder="State/Province" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="City" value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} placeholder="City" />
            <Input label="Zip Code" value={formData.zip_code} onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))} placeholder="Zip/Postal code" />
          </div>
        </form>
      </Modal>

      {/* University Details Modal */}
      <Modal 
        isOpen={detailsModalOpen} 
        onClose={closeDetailsModal} 
        title="University Details" 
        size="xl"
      >
        {selectedUniversity && (
          <div className="space-y-6">
            {/* University Info */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">{selectedUniversity.name}</h3>
              <p className="text-slate-600 mb-4">{selectedUniversity.description}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-medium">{[selectedUniversity.city, selectedUniversity.state, selectedUniversity.country].filter(Boolean).join(', ') || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Established</p>
                  <p className="font-medium">{selectedUniversity.established_on ? new Date(selectedUniversity.established_on).getFullYear() : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Students</p>
                  <p className="font-medium">{selectedUniversity.students_capacity?.toLocaleString() || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Faculty</p>
                  <p className="font-medium">{selectedUniversity.numbers_of_faculty?.toLocaleString() || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Courses List */}
            <div>
              <h4 className="text-md font-semibold text-slate-800 mb-3">
                Courses ({selectedUniversity.courses?.length || 0})
              </h4>
              {selectedUniversity.courses && selectedUniversity.courses.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedUniversity.courses.map((course) => (
                    <div key={course.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h5 className="font-medium text-slate-800">{course.title}</h5>
                          <p className="text-sm text-slate-600 line-clamp-2">{course.description}</p>
                        </div>
                        <Badge variant={course.status === 'published' ? 'success' : course.status === 'draft' ? 'warning' : 'default'}>
                          {course.status}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
                        <div>
                          <p className="text-slate-500">Category</p>
                          <p className="font-medium">{course.category?.name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Level</p>
                          <p className="font-medium capitalize">{course.level || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Price</p>
                          <p className="font-medium">{course.price} {course.currency}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Enrollments</p>
                          <p className="font-medium">{course.enrollment_count || 0}</p>
                        </div>
                      </div>

                      {course.category && (
                        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                          <span>Category Country: {course.category.country?.name || 'N/A'}</span>
                          <span className="mx-2">•</span>
                          <span>Status: {course.category.status?.name || 'N/A'}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No courses available for this university</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete University" message="Are you sure you want to delete this university?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default UniversityList;
